// Copyright 2025, University of Maryland, All Rights Reserved
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { BillSections } from "./BillSections"
import { Loader2, Printer } from "lucide-react"
import type { JSONSchema } from '@/api/types/schema'
import { generatePrintHTML } from '@/utils/print-template'
import { renderBillDataToHTML } from '@/utils/bill-html-renderer'

interface ReportDialogProps {
  title: string
  report_data: any
  open: boolean
  onOpenChange: (open: boolean) => void
  isLoading?: boolean
  schema?: JSONSchema
}

export function ReportDialog({ title, report_data, open, onOpenChange, isLoading, schema }: ReportDialogProps) {
  const handlePrint = () => {
    // Generate consistent HTML content using the same renderer as bulk print
    const content = renderBillDataToHTML(report_data, schema);
    
    // Create a fullscreen window for printing
    const printWindow = window.open('', '_blank', `width=${screen.width},height=${screen.height},fullscreen=yes,scrollbars=yes`);
    
    if (!printWindow) {
      console.error('Could not open print window');
      return;
    }
    
    // Generate and write the HTML to the new window
    const printHTML = generatePrintHTML(title, content);
    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] w-[95vw] p-0 flex flex-col report-dialog-content">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0 bg-background/95 backdrop-blur-sm print:border-none print:bg-transparent">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl pr-8">{title}</DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              disabled={isLoading}
              className="print:hidden flex items-center gap-2"
              id="print-button"
            >
              <Printer className="h-4 w-4" />
              Print
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 scrollbar-thin scrollbar-thumb-border scrollbar-track-background">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mr-2" />
              <span>Loading report...</span>
            </div>
          ) : (
            <BillSections report_data={report_data} schema={schema} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
